// Single source of truth for app state. Vanilla TS, no framework —
// keep all mutation funnelled through `update()` so re-renders are
// predictable and `URL.revokeObjectURL` teardown happens in one place.

import type { ChipFontKey } from "./overlay/chip.js";
import type {
  ChipStyle,
  Layout,
  ThemeKey,
  Variant,
} from "./overlay/constants.generated.js";
import type { Position } from "./overlay/positions.js";

export type Phase = "idle" | "processing" | "done";

export interface InputFile {
  id: string; // crypto.randomUUID
  file: File; // original File handle (incl. HEIC blobs before decode)
  thumbUrl?: string; // object URL for input thumbnail (revoke on remove)
}

export interface ResultItem {
  id: string;
  filename: string;
  blob: Blob;
  thumbUrl: string; // object URL; revoke on grid teardown
  inputId: string; // back-reference for grouping
  selected: boolean;
}

export interface LogoOptions {
  positions: Position[]; // multi-select (≥1)
  variant: Variant | "auto";
  layout: Layout;
}

export interface HashtagOptions {
  text: string;
  positions: Position[]; // multi-select (≥1)
  fontKey: ChipFontKey;
  fillMode: "theme" | "custom";
  theme: ThemeKey;
  customColor: string; // #RRGGBB
  textColor: string;   // #RRGGBB
  chipStyle: ChipStyle;
}

export interface ProcessProgress {
  current: number;
  total: number;
  abortController: AbortController;
}

export interface AppState {
  phase: Phase;
  inputs: InputFile[];
  logo: LogoOptions;
  hashtag: HashtagOptions;
  results: ResultItem[];
  progress: ProcessProgress | null;
  errorMessage: string | null;
  /** Result id currently shown in the lightbox; null = closed. */
  lightboxResultId: string | null;
}

export function makeInitialState(): AppState {
  return {
    phase: "idle",
    inputs: [],
    logo: {
      positions: ["top-left"],
      variant: "auto",
      layout: "DOIT_01",
    },
    hashtag: {
      text: "#未來生活解密",
      positions: ["top-right"],
      fontKey: "gensen-rounded",
      fillMode: "theme",
      theme: "future_life",
      customColor: "#7C3AED",
      textColor: "#FFFFFF",
      chipStyle: "tight",
    },
    results: [],
    progress: null,
    errorMessage: null,
    lightboxResultId: null,
  };
}

/** Revoke an object URL safely (skipping `undefined` / missing). */
export function revokeUrl(url: string | undefined): void {
  if (url) URL.revokeObjectURL(url);
}

/**
 * Teardown helper — revokes every object URL on the result grid.
 * Call before replacing `state.results` (e.g. on second Process click).
 */
export function teardownResults(results: ResultItem[]): void {
  for (const r of results) revokeUrl(r.thumbUrl);
}

/** Teardown helper for input thumbnails (called on remove). */
export function teardownInputs(inputs: InputFile[]): void {
  for (const inp of inputs) revokeUrl(inp.thumbUrl);
}
